/**
 * Утилитарные функции для расчета теплопотерь здания
 */

/**
 * Основная функция расчета теплопотерь здания
 * 
 * @param {Object} formData - Данные формы с параметрами здания
 * @param {Object} buildingMaterials - Данные о теплофизических свойствах строительных материалов
 * @param {Object} windowTypes - Данные о теплофизических свойствах окон
 * @returns {Object} Объект с результатами расчета теплопотерь
 */
export const calculateHeatLoss = (formData, buildingMaterials, windowTypes) => {
    // Парсинг числовых значений из формы
    const floorArea = parseFloat(formData.floorArea);
    const ceilingHeight = parseFloat(formData.ceilingHeight);
    const floors = parseInt(formData.floors);
    const wallThickness = parseFloat(formData.wallThickness) / 1000; // перевод в метры
    const insulationThickness = parseFloat(formData.insulationThickness) / 1000; // перевод в метры
    const windowArea = parseFloat(formData.windowArea);
    const roofInsulationThickness = parseFloat(formData.roofInsulationThickness) / 1000; // перевод в метры
    const floorInsulationThickness = parseFloat(formData.floorInsulationThickness) / 1000; // перевод в метры
    const outdoorTemperature = parseFloat(formData.outdoorTemperature);
    const indoorTemperature = parseFloat(formData.indoorTemperature);
    const temperatureDifference = indoorTemperature - outdoorTemperature;
    
    // Расчет объема помещения
    const volume = floorArea * ceilingHeight * floors;
    
    // Расчет площади наружных стен (периметр * высоту * этажи)
    // Предполагаем квадратную форму для простоты
    const perimeter = Math.sqrt(floorArea) * 4;
    const wallsHeight = ceilingHeight * floors;
    const wallsArea = perimeter * wallsHeight;
    
    // Расчет площади стен без учета окон
    const netWallsArea = wallsArea - windowArea;
    
    // Расчет площади крыши/потолка и пола
    // Предполагаем, что они равны площади пола
    const roofArea = floorArea;
    const floorAreaForCalc = floorArea;
    
    // Определение коэффициентов теплопередачи (U-values)
    const wallsUValue = calculateWallUValue(
      formData.wallMaterial,
      wallThickness,
      formData.insulationMaterial,
      insulationThickness,
      buildingMaterials,
      formData.includeThermalBridges
    );
    
    const windowsUValue = calculateWindowUValue(formData.windowType, windowTypes);
    
    const roofUValue = calculateRoofUValue(
      formData.roofType,
      formData.roofMaterial,
      roofInsulationThickness,
      buildingMaterials,
      formData.includeThermalBridges
    );
    
    const floorUValue = calculateFloorUValue(
      formData.floorType,
      formData.floorMaterial,
      floorInsulationThickness,
      buildingMaterials,
      formData.includeThermalBridges
    );
    
    // Расчет интенсивности воздухообмена
    const airChangeRate = calculateAirChangeRate(
      formData.ventilationType,
      formData.infiltrationRate
    );
    
    // Расчет теплопотерь через стены
    const wallsHeatLoss = netWallsArea * wallsUValue * temperatureDifference;
    
    // Расчет теплопотерь через окна
    const windowsHeatLoss = windowArea * windowsUValue * temperatureDifference;
    
    // Расчет теплопотерь через крышу/потолок
    const roofHeatLoss = (formData.roofType === 'adjacent') ? 
      0 : // если сверху соседнее помещение, то потерь нет
      roofArea * roofUValue * temperatureDifference;
    
    // Расчет теплопотерь через пол
    const floorHeatLoss = (formData.floorType === 'adjacent') ? 
      0 : // если снизу соседнее помещение, то потерь нет
      floorAreaForCalc * floorUValue * temperatureDifference;
    
    // Расчет теплопотерь на вентиляцию и инфильтрацию
    // Формула: V * ACH * 0.33 * ΔT
    // где 0.33 - удельная теплоемкость воздуха (Вт·ч/(м³·°C))
    let ventilationHeatLoss = volume * airChangeRate * 0.33 * temperatureDifference;
    
    // Учет рекуперации тепла, если она есть
    if (formData.hasHeatRecovery && formData.ventilationType === 'mechanical_recovery') {
      const recoveryEfficiency = parseFloat(formData.recoveryEfficiency) / 100;
      ventilationHeatLoss *= (1 - recoveryEfficiency);
    }
    
    // Учет температурного фактора - увеличение потерь на 10%
    const temperatureFactorMultiplier = formData.includeTemperatureFactor ? 1.1 : 1.0;
    
    // Суммарные теплопотери с учетом температурного фактора
    const totalHeatLoss = (wallsHeatLoss + 
                           windowsHeatLoss + 
                           roofHeatLoss + 
                           floorHeatLoss + 
                           ventilationHeatLoss) * temperatureFactorMultiplier;
    
    // Расчет удельных теплопотерь на квадратный метр
    const specificHeatLoss = totalHeatLoss / floorArea;
    
    // Расчет процентного распределения теплопотерь
    const heatLossPercentages = {
      walls: (wallsHeatLoss / totalHeatLoss) * 100,
      windows: (windowsHeatLoss / totalHeatLoss) * 100,
      roof: (roofHeatLoss / totalHeatLoss) * 100,
      floor: (floorHeatLoss / totalHeatLoss) * 100,
      ventilation: (ventilationHeatLoss / totalHeatLoss) * 100
    };
    
    // Расчет рекомендуемой мощности системы отопления с 20% запасом
    const heatingSafetyFactor = 1.2;
    const recommendedHeatingCapacity = (totalHeatLoss / 1000) * heatingSafetyFactor; // в кВт
    
    // Расчет среднегодовой температуры для выбранного региона
    // В реальном приложении здесь был бы запрос к API или базе данных
    const avgAnnualTemperature = estimateAverageAnnualTemperature(formData.region, outdoorTemperature);
    
    // Расчет годового потребления энергии на отопление
    const annualHeatingDegreeHours = calculateHeatingDegreeHours(indoorTemperature, avgAnnualTemperature);
    const annualEnergyConsumption = (totalHeatLoss / temperatureDifference) * annualHeatingDegreeHours / 1000; // в кВт·ч
    
    // Расчет удельного расхода энергии на отопление
    const specificEnergyConsumption = annualEnergyConsumption / floorArea; // в кВт·ч/м²
    
    // Определение класса энергоэффективности
    const energyEfficiencyRating = determineEnergyEfficiencyClass(specificEnergyConsumption);
    
    // Рекомендации по улучшению энергоэффективности
    const recommendations = generateRecommendations(
      wallsUValue, 
      windowsUValue, 
      roofUValue, 
      floorUValue, 
      formData.ventilationType, 
      formData.hasHeatRecovery
    );
    
    // Определение описательных текстов для крыши и пола
    const roofDescription = formData.roofType === 'adjacent' ? 'Потолок (соседнее помещение)' :
                           formData.roofType === 'flat' ? 'Плоская крыша' :
                           formData.roofType === 'insulated' ? 'Утепленная крыша' : 'Крыша/чердак';
                          
    const floorDescription = formData.floorType === 'adjacent' ? 'Пол (соседнее помещение)' :
                            formData.floorType === 'ground' ? 'Пол по грунту' :
                            formData.floorType === 'insulated' ? 'Утепленный пол' : 'Пол над подвалом';
    
    // Расчет общего коэффициента теплопередачи здания
    const totalArea = netWallsArea + windowArea + roofArea + floorAreaForCalc;
    const overallUValue = ((netWallsArea * wallsUValue) +
                           (windowArea * windowsUValue) +
                           (roofArea * roofUValue) +
                           (floorAreaForCalc * floorUValue)) / totalArea;
    
    // Расчет соотношения площади окон к площади стен
    const windowWallRatio = windowArea / wallsArea;
    
    // Формирование и возврат результатов расчета
    return {
      // Основные результаты
      totalHeatLoss,                 // Общие теплопотери в Вт
      specificHeatLoss,              // Удельные теплопотери в Вт/м²
      heatLosses: {                 // Теплопотери по элементам здания
        walls: wallsHeatLoss,
        windows: windowsHeatLoss,
        roof: roofHeatLoss,
        floor: floorHeatLoss,
        ventilation: ventilationHeatLoss
      },
      heatLossPercentages,          // Процентное распределение теплопотерь
      
      // Коэффициенты теплопередачи (U-values)
      uValues: {
        walls: wallsUValue,
        windows: windowsUValue,
        roof: roofUValue,
        floor: floorUValue
      },
      overallUValue,                // Общий коэффициент теплопередачи здания
      
      // Параметры системы отопления
      recommendedHeatingCapacity,   // Рекомендуемая мощность системы отопления в кВт
      heatingSafetyFactor,          // Коэффициент запаса мощности
      annualEnergyConsumption,      // Годовое потребление энергии в кВт·ч
      specificEnergyConsumption,    // Удельный расход энергии в кВт·ч/м²·год
      energyEfficiencyRating,       // Класс энергоэффективности
      
      // Геометрические параметры
      areas: {
        walls: netWallsArea,
        windows: windowArea,
        roof: roofArea,
        floor: floorAreaForCalc
      },
      volume,                       // Объем помещения
      windowWallRatio,              // Соотношение площади окон к площади стен
      
      // Параметры микроклимата
      outdoorTemperature,           // Расчетная температура наружного воздуха
      indoorTemperature,            // Расчетная температура внутри помещения
      temperatureDifference,        // Разница температур
      airChangeRate,                // Кратность воздухообмена
      avgAnnualTemperature,         // Среднегодовая температура
      
      // Дополнительные параметры
      roofDescription,              // Описание типа крыши/потолка
      floorDescription,             // Описание типа пола
      recommendations               // Рекомендации по улучшению энергоэффективности
    };
  };
  
  /**
   * Расчет коэффициента теплопередачи стены
   * 
   * @param {string} wallMaterial - Тип стенового материала
   * @param {number} wallThickness - Толщина стены в метрах
   * @param {string} insulationMaterial - Тип утеплителя
   * @param {number} insulationThickness - Толщина утеплителя в метрах
   * @param {Object} buildingMaterials - Данные о теплопроводности материалов
   * @param {boolean} includeThermalBridges - Учитывать ли тепловые мосты
   * @returns {number} Коэффициент теплопередачи стены в Вт/(м²·°C)
   */
  const calculateWallUValue = (wallMaterial, wallThickness, insulationMaterial, insulationThickness, buildingMaterials, includeThermalBridges) => {
    // Коэффициенты теплоотдачи внутренней и наружной поверхностей
    const internalResistance = 1 / 8.7;  // м²·°C/Вт
    const externalResistance = 1 / 23;   // м²·°C/Вт
    
    // Теплопроводность основного материала стены
    let wallConductivity = 0.7; // значение по умолчанию Вт/(м·°C)
    
    // Если есть данные о материалах стен
    if (buildingMaterials && 
        buildingMaterials.walls && 
        buildingMaterials.walls[wallMaterial] && 
        buildingMaterials.walls[wallMaterial].conductivity) {
      wallConductivity = buildingMaterials.walls[wallMaterial].conductivity;
    }
    
    // Термическое сопротивление стены
    const wallResistance = wallThickness / wallConductivity;
    
    // Термическое сопротивление утеплителя (если есть)
    let insulationResistance = 0;
    if (insulationMaterial !== 'none' && insulationThickness > 0) {
      let insulationConductivity = 0.04; // значение по умолчанию Вт/(м·°C)
      
      // Если есть данные о теплоизоляционных материалах
      if (buildingMaterials && 
          buildingMaterials.insulation && 
          buildingMaterials.insulation[insulationMaterial] && 
          buildingMaterials.insulation[insulationMaterial].conductivity) {
        insulationConductivity = buildingMaterials.insulation[insulationMaterial].conductivity;
      }
      
      insulationResistance = insulationThickness / insulationConductivity;
    }
    
    // Суммарное термическое сопротивление
    const totalResistance = internalResistance + wallResistance + insulationResistance + externalResistance;
    
    // Коэффициент теплопередачи (U-value)
    let uValue = 1 / totalResistance;
    
    // Учет тепловых мостов (увеличение U-value на 10-15%)
    if (includeThermalBridges) {
      uValue *= 1.15;
    }
    
    return uValue;
  };
  
  /**
   * Расчет коэффициента теплопередачи окна
   * 
   * @param {string} windowType - Тип остекления
   * @param {Object} windowTypes - Данные о теплопроводности окон
   * @returns {number} Коэффициент теплопередачи окна в Вт/(м²·°C)
   */
  const calculateWindowUValue = (windowType, windowTypes) => {
    // Предопределенные U-values для различных типов окон
    const defaultUValues = {
      single_glazed: 5.8,
      double_glazed_old: 2.9,
      double_glazed_modern: 1.5,
      double_glazed_low_e: 1.3,
      triple_glazed: 1.0,
      triple_glazed_low_e: 0.8,
      triple_glazed_argon: 0.6
    };
    
    // Возвращаем соответствующее значение или значение по умолчанию
    if (windowTypes && windowTypes[windowType] && windowTypes[windowType].uValue) {
      return windowTypes[windowType].uValue;
    }
    
    return defaultUValues[windowType] || 1.5;
  };
  
  const calculateRoofUValue = (roofType, roofMaterial, insulationThickness, buildingMaterials, includeThermalBridges) => {
    // Если соседнее помещение сверху, то теплопотерь нет
    if (roofType === 'adjacent') {
      return 0.1; // Минимальное значение для расчетов
    }
    
    // Коэффициенты теплоотдачи внутренней и наружной поверхностей
    const internalResistance = 1 / 8.7;  // м²·°C/Вт
    const externalResistance = 1 / 23;   // м²·°C/Вт
    
    // Получаем теплопроводность материала крыши/потолка
    let roofConductivity = 0.5; // значение по умолчанию Вт/(м·°C)
    let roofThickness = 0.2; // значение по умолчанию м
    
    // Если есть данные о материалах кровли/потолка
    if (buildingMaterials && 
        buildingMaterials.roofs && 
        buildingMaterials.roofs[roofMaterial]) {
      
      if (buildingMaterials.roofs[roofMaterial].conductivity) {
        roofConductivity = buildingMaterials.roofs[roofMaterial].conductivity;
      }
      
      if (buildingMaterials.roofs[roofMaterial].thickness) {
        roofThickness = buildingMaterials.roofs[roofMaterial].thickness;
      }
    }
    
    // Термическое сопротивление основного материала
    const roofResistance = roofThickness / roofConductivity;
    
    // Дополнительное сопротивление в зависимости от типа
    let additionalResistance = 0;
    if (roofType === 'insulated' || roofType === 'flat') {
      // Теплопроводность утеплителя (минеральная вата)
      const insulationConductivity = 0.04; // Вт/(м·°C)
      additionalResistance = insulationThickness / insulationConductivity;
    } else if (roofType === 'exposed') {
      // Сопротивление холодного чердака
      additionalResistance = 0.2;
    }
    
    // Суммарное термическое сопротивление
    const totalResistance = internalResistance + roofResistance + additionalResistance + externalResistance;
    
    // Коэффициент теплопередачи (U-value)
    let uValue = 1 / totalResistance;
    
    // Учет тепловых мостов (увеличение U-value на 5-10%)
    if (includeThermalBridges) {
      uValue *= 1.1;
    }
    
    return uValue;
  };
  
  /**
   * Расчет коэффициента теплопередачи пола
   * 
   * @param {string} floorType - Тип пола
   * @param {string} floorMaterial - Материал пола
   * @param {number} insulationThickness - Толщина утеплителя в метрах
   * @param {Object} buildingMaterials - Данные о теплопроводности материалов
   * @param {boolean} includeThermalBridges - Учитывать ли тепловые мосты
   * @returns {number} Коэффициент теплопередачи пола в Вт/(м²·°C)
   */
  const calculateFloorUValue = (floorType, floorMaterial, insulationThickness, buildingMaterials, includeThermalBridges) => {
    // Если соседнее помещение снизу, то теплопотерь нет
    if (floorType === 'adjacent') {
      return 0.1; // Минимальное значение для расчетов
    }
    
    // Коэффициенты теплоотдачи внутренней и наружной поверхностей
    const internalResistance = 1 / 8.7;  // м²·°C/Вт
    
    // Для пола по грунту вместо внешней теплоотдачи используем сопротивление грунта
    const externalResistance = floorType === 'ground' ? 0.5 : 1 / 23;  // м²·°C/Вт
    
    // Получаем теплопроводность материала пола
    let floorConductivity = 0.5; // значение по умолчанию Вт/(м·°C)
    let floorThickness = 0.2; // значение по умолчанию м
    
    // Если есть данные о материалах пола
    if (buildingMaterials && 
        buildingMaterials.floors && 
        buildingMaterials.floors[floorMaterial]) {
      
      if (buildingMaterials.floors[floorMaterial].conductivity) {
        floorConductivity = buildingMaterials.floors[floorMaterial].conductivity;
      }
      
      if (buildingMaterials.floors[floorMaterial].thickness) {
        floorThickness = buildingMaterials.floors[floorMaterial].thickness;
      }
    }
    
    // Термическое сопротивление основного материала
    const floorResistance = floorThickness / floorConductivity;
    
    // Дополнительное сопротивление утеплителя
    let additionalResistance = 0;
    if (floorType === 'insulated') {
      // Теплопроводность утеплителя (минеральная вата)
      const insulationConductivity = 0.04; // Вт/(м·°C)
      additionalResistance = insulationThickness / insulationConductivity;
    }
    
    // Суммарное термическое сопротивление
    const totalResistance = internalResistance + floorResistance + additionalResistance + externalResistance;
    
    // Коэффициент теплопередачи (U-value)
    let uValue = 1 / totalResistance;
    
    // Учет тепловых мостов (увеличение U-value на 5-10%)
    if (includeThermalBridges) {
      uValue *= 1.1;
    }
    
    return uValue;
  };
  
  const calculateAirChangeRate = (ventilationType, infiltrationRate) => {
    // Базовые значения для различных типов вентиляции
    let baseRate = 0;
    
    switch (ventilationType) {
      case 'natural':
        baseRate = 0.5; // 0.5 объема помещения в час
        break;
      case 'mechanical':
      case 'mechanical_recovery':
        baseRate = 0.7; // 0.7 объема помещения в час
        break;
      default:
        baseRate = 0.5;
    }
    
    // Коэффициенты инфильтрации
    let infiltrationFactor = 1.0;
    
    switch (infiltrationRate) {
      case 'low':
        infiltrationFactor = 0.7;
        break;
      case 'medium':
        infiltrationFactor = 1.0;
        break;
      case 'high':
        infiltrationFactor = 1.5;
        break;
      default:
        infiltrationFactor = 1.0;
    }
    
    // Итоговая кратность воздухообмена
    return baseRate * infiltrationFactor;
  };
  
  /**
   * Оценка среднегодовой температуры для выбранного региона
   * 
   * @param {string} region - Идентификатор региона
   * @param {number} designTemperature - Расчетная температура наружного воздуха
   * @returns {number} Среднегодовая температура в °C
   */
  const estimateAverageAnnualTemperature = (region, designTemperature) => {
    // Примерное соотношение среднегодовой температуры и расчетной
    // В реальном приложении здесь должна быть база данных климатических параметров
    const regionalData = {
      'moscow': 5.4,
      'saint_petersburg': 5.8,
      'novosibirsk': 1.3,
      'yekaterinburg': 2.6,
      'krasnodar': 12.1,
      'vladivostok': 4.9,
      'murmansk': 0.2,
      'yakutsk': -8.8
      // другие регионы...
    };
    
    // Возвращаем среднегодовую температуру для известного региона или оценку
    return regionalData[region] || (designTemperature / 3 + 15);
  };
  
  /**
   * Расчет градусо-часов отопительного периода
   * 
   * @param {number} indoorTemperature - Температура внутри помещения
   * @param {number} avgAnnualTemperature - Среднегодовая температура
   * @returns {number} Градусо-часы отопительного периода
   */
  const calculateHeatingDegreeHours = (indoorTemperature, avgAnnualTemperature) => {
    // Оценка продолжительности отопительного сезона в зависимости от среднегодовой температуры
    let heatingDays = 0;
    
    if (avgAnnualTemperature < 0) {
      heatingDays = 250; // очень холодный климат
    } else if (avgAnnualTemperature < 5) {
      heatingDays = 220; // холодный климат
    } else if (avgAnnualTemperature < 10) {
      heatingDays = 190; // умеренный климат
    } else {
      heatingDays = 150; // теплый климат
    }
    
    // Расчет средней температуры отопительного периода
    const avgHeatingSeasonTemperature = avgAnnualTemperature - 5; // приблизительная оценка
    
    // Расчет градусо-дней отопительного периода
    const degreeHours = (indoorTemperature - avgHeatingSeasonTemperature) * 24 * heatingDays;
    
    return degreeHours;
  };
  
  const determineEnergyEfficiencyClass = (specificEnergyConsumption) => {
    // Соответствие классов энергоэффективности удельному расходу энергии
    // по Приказу Минстроя России от 17.11.2017 № 1550/пр
    if (specificEnergyConsumption < 45) {
      return 'A++';
    } else if (specificEnergyConsumption < 65) {
      return 'A+';
    } else if (specificEnergyConsumption < 85) {
      return 'A';
    } else if (specificEnergyConsumption < 105) {
      return 'B';
    } else if (specificEnergyConsumption < 135) {
      return 'C';
    } else if (specificEnergyConsumption < 175) {
      return 'D';
    } else if (specificEnergyConsumption < 225) {
      return 'E';
    } else if (specificEnergyConsumption < 275) {
      return 'F';
    } else {
      return 'G';
    }
  };
  
  /**
   * Генерация рекомендаций по улучшению энергоэффективности
   * 
   * @param {number} wallsUValue - Коэффициент теплопередачи стен
   * @param {number} windowsUValue - Коэффициент теплопередачи окон
   * @param {number} roofUValue - Коэффициент теплопередачи крыши
   * @param {number} floorUValue - Коэффициент теплопередачи пола
   * @param {string} ventilationType - Тип вентиляции
   * @param {boolean} hasHeatRecovery - Наличие рекуперации тепла
   * @returns {Array} Массив рекомендаций
   */
  const generateRecommendations = (wallsUValue, windowsUValue, roofUValue, floorUValue, ventilationType, hasHeatRecovery) => {
    const recommendations = [];
    
    // Рекомендации по утеплению стен
    if (wallsUValue > 0.3) {
      recommendations.push('Рекомендуется дополнительное утепление стен. Целевое значение: 0.15-0.25 Вт/(м²·°C).');
    }
    
    // Рекомендации по окнам
    if (windowsUValue > 1.5) {
      recommendations.push('Рекомендуется замена окон на энергоэффективные (двух- или трехкамерные стеклопакеты). Целевое значение: 0.8-1.3 Вт/(м²·°C).');
    }
    
    // Рекомендации по крыше
    if (roofUValue > 0.25 && roofUValue < 2.0) {
      recommendations.push('Рекомендуется дополнительное утепление крыши/потолка. Целевое значение: 0.12-0.18 Вт/(м²·°C).');
    }
    
    // Рекомендации по полу
    if (floorUValue > 0.3 && floorUValue < 2.0) {
      recommendations.push('Рекомендуется дополнительное утепление пола. Целевое значение: 0.15-0.25 Вт/(м²·°C).');
    }
    
    // Рекомендации по вентиляции
    if (ventilationType === 'natural') {
      recommendations.push('Рекомендуется установка механической вентиляции с рекуперацией тепла для снижения теплопотерь при воздухообмене.');
    } else if (ventilationType === 'mechanical' && !hasHeatRecovery) {
      recommendations.push('Рекомендуется модернизация системы вентиляции с добавлением рекуператора тепла с эффективностью не менее 75%.');
    }
    
    // Если нет очевидных проблем
    if (recommendations.length === 0) {
      recommendations.push('Здание имеет хорошие показатели теплоизоляции. Для дальнейшего повышения энергоэффективности рассмотрите применение новейших технологий и материалов.');
    }
    
    return recommendations;
  };